import React from "react";

import "./Textbox.scss";
import Utility from "../../foundation/Utility";

type TextboxType = "text" | "number" | "email" | "password" | "tel";

interface TextboxProps {
    id?: string;
    type?: TextboxType;
    label?: string;
    value?: string;
    min?: number;
    max?: number;
    width?: number | string;
    fullWidth?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    allowNull?: boolean;
    naForNull?: boolean;
    decimalPlaces?: number;
    decimalIncrement?: number;
    className?: string;
    autoComplete?: string;
    placeholder?: string;
    tooltip?: string;
    tooltipPlace?: string;
    onChange?: (value: string) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyDownCapture?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyUpCapture?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

interface TextboxState {
    ref: React.RefObject<HTMLInputElement>;
    value?: string;
}

class Textbox extends React.Component<TextboxProps, TextboxState> {
    state: TextboxState = {
        ref: React.createRef<HTMLInputElement>(),
    };

    componentDidMount(): void {
        let value = Utility.notNull(this.props.value)
            ? String(this.props.value)
            : this.props.value;
        if (this.props.type === "number") {
            this.numberParse(value, true);
        } else {
            this.setState({ value: value });
        }
    }

    componentDidUpdate(prevProps: Readonly<TextboxProps>): void {
        if (prevProps.value !== this.props.value) {
            let value = Utility.notNull(this.props.value)
                ? String(this.props.value)
                : this.props.value;
            if (this.props.type === "number") {
                this.numberParse(value, true);
            } else {
                this.setState({ value: value });
            }
        }
    }

    focus() {
        this.state.ref.current?.focus();
    }

    onFocus() {
        setTimeout(() => this.state.ref.current?.select(), 100);
    }

    onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "ArrowUp":
                this.increment();
                event.preventDefault();
                event.stopPropagation();
                break;
            case "ArrowDown":
                this.decrement();
                event.preventDefault();
                event.stopPropagation();
                break;
            case "0":
            case "1":
            case "2":
            case "3":
            case "4":
            case "5":
            case "6":
            case "7":
            case "8":
            case "9":
            case ".":
            case "N":
            case "n":
            case "A":
            case "a":
            case "Tab":
            case "Backspace":
            case "Delete":
            case "Enter":
                this.props.onKeyDownCapture?.(event);
                break;
            default:
                if (this.props.type === "number") {
                    event.preventDefault();
                    event.stopPropagation();
                } else {
                    this.props.onKeyDownCapture?.(event);
                }
                break;
        }
    }

    onKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "ArrowUp":
                if (this.props.type === "number") {
                    event.preventDefault();
                    event.stopPropagation();
                }
                break;
            case "ArrowDown":
                if (this.props.type === "number") {
                    event.preventDefault();
                    event.stopPropagation();
                }
                break;
            default:
                this.props.onKeyUpCapture?.(event);
                break;
        }
    }

    getIncrementValue() {
        if (this.props.decimalIncrement !== undefined)
            return this.props.decimalIncrement;

        switch (this.props.decimalPlaces) {
            case 0:
                return 1;
            case 1:
                return 0.1;
            case 2:
                return 0.01;
            case 3:
                return 0.001;
            default:
                return 1;
        }
    }

    increment() {
        if (this.props.type !== "number") return;

        const currentValue = parseFloat(this.state.value) || 0;
        const newValue = Utility.notNull(this.props.max)
            ? Math.min(currentValue + this.getIncrementValue(), this.props.max)
            : currentValue + this.getIncrementValue();
        this.numberParse(String(newValue));
    }

    decrement() {
        if (this.props.type !== "number") return;

        const currentValue = parseFloat(this.state.value) || 0;
        const newValue = Utility.notNull(this.props.min)
            ? Math.max(currentValue - this.getIncrementValue(), this.props.min)
            : currentValue - this.getIncrementValue();
        this.numberParse(String(newValue));
    }

    changed(value: string) {
        if (this.props.type === "number") {
            this.setState({ value: value });

            this.debouncedNumberParse(value);
        } else {
            this.props.onChange?.(value);
        }
    }

    debouncedNumberParse = Utility.debounce(() => {
        this.numberParse(this.state.value);
    }, 500);
    numberParse(value: string, internal: boolean = false) {
        if (!!value && value.toLowerCase() === "na") {
            if (this.props.allowNull && this.props.naForNull) value = undefined;
            else value = "0";
        }
        if (this.props.allowNull && !value?.trim()) {
            value = undefined;
        }
        if (this.props.decimalPlaces !== undefined) {
            const numericValue = parseFloat(value);
            if (!isNaN(numericValue)) {
                value = numericValue.toFixed(this.props.decimalPlaces);
            }
        }
        if (this.props.naForNull && !value) value = "NA";

        this.setState({ value: value });

        if (!internal)
            this.props.onChange?.(value === "NA" ? undefined : value);
    }

    render(): React.ReactNode {
        let classes = `textbox ${this.props.type ?? "text"}`;
        if (this.props.className) classes += ` ${this.props.className}`;

        let inputClasses = "";
        if (this.props.fullWidth) {
            inputClasses += " full";
        }

        return (
            <label
                className={classes}
                style={{ width: this.props.width }}
                data-html={true}
                data-tip={this.props.tooltip}
                data-place={this.props.tooltipPlace}>
                {this.props.label && (
                    <span className="text">{this.props.label}</span>
                )}
                <input
                    id={this.props.id}
                    ref={this.state.ref}
                    className={inputClasses}
                    type={this.props.type === "password" ? "password" : "text"}
                    value={
                        this.state.value ?? (this.props.naForNull ? "NA" : "")
                    }
                    min={this.props.min}
                    max={this.props.max}
                    placeholder={this.props.placeholder}
                    autoComplete={this.props.autoComplete}
                    disabled={this.props.disabled === true}
                    readOnly={this.props.readonly === true}
                    onFocus={this.onFocus.bind(this)}
                    onChange={ev => this.changed(ev.target.value)}
                    onKeyDown={this.props.onKeyDown}
                    onKeyUp={this.props.onKeyUp}
                    onKeyDownCapture={this.onKeyDown.bind(this)}
                    onKeyUpCapture={this.onKeyUp.bind(this)}
                />

                {this.props.type === "number" && (
                    <span className="number-arrows">
                        <span
                            className="arrow up"
                            tabIndex={0}
                            onClick={this.increment.bind(this)}
                        />
                        <span
                            className="arrow down"
                            tabIndex={0}
                            onClick={this.decrement.bind(this)}
                        />
                    </span>
                )}
            </label>
        );
    }
}

export default Textbox;
